import { createContext, useContext, useEffect, useState } from "react";

import { ContextWrapComponentProps } from "./types";

const MOBILE_BREAKPOINT = 768;

interface ViewportContextInterface {
  isMobile: boolean;
  resetViewportScale: () => void;
}

const ViewportContext = createContext<ViewportContextInterface | null>(null);

const hasMatchMedia = typeof window.matchMedia === "function";
const mobileQuery = `(max-width: ${MOBILE_BREAKPOINT}px)`;

const ViewportContextWrapper = ({ children }: ContextWrapComponentProps) => {
  const [isMobile, setIsMobile] = useState(
    () => hasMatchMedia && window.matchMedia(mobileQuery).matches
  );

  useEffect(() => {
    if (!hasMatchMedia) return;

    const mediaQuery = window.matchMedia(mobileQuery);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handler);

    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // after a manual pinch-zoom+unzoom cycle the visual viewport
  // scale drifts slightly from 1.0. Toggling maximum-scale on blur forces a
  // recalculation that snaps scale back to 1.
  const resetViewportScale = () => {
    if (!isMobile) return;

    const meta = document.querySelector<HTMLMetaElement>(
      "meta[name='viewport']"
    );
    if (meta) {
      const original = meta.content;
      meta.content = original + ", maximum-scale=1";
      requestAnimationFrame(() => {
        meta.content = original;
      });
    }
  };

  return (
    <ViewportContext.Provider value={{ isMobile, resetViewportScale }}>
      {children}
    </ViewportContext.Provider>
  );
};

export const useViewport = (): ViewportContextInterface => {
  const context = useContext(ViewportContext);

  if (!context) {
    throw new Error("useViewport must be used within ViewportContextWrapper");
  }

  return context;
};

export default ViewportContextWrapper;
